import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ShoppingCart, Euro, Users, ArrowRight, CalendarClock } from "lucide-react";
import api, { eur } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-sm border border-stone p-6">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.18em] text-ash">{label}</p>
      <Icon className="h-5 w-5 text-copper" strokeWidth={1.5} />
    </div>
    <p className="font-serif text-4xl text-ink mt-4">{value}</p>
  </div>
);

const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR") : "");

const NOTICE_STYLES = {
  warning: "bg-amber-50 border-amber-300 text-amber-900",
  danger: "bg-red-50 border-red-300 text-red-900",
  blocked: "bg-red-600 border-red-700 text-white",
};

function SubscriptionNotice({ notice }) {
  const left = notice.days_left;
  const title =
    notice.level === "blocked"
      ? notice.reason === "expired"
        ? "Abonnement expiré — la boutique est suspendue"
        : "La boutique est suspendue"
      : left === 0
        ? "Votre abonnement se termine aujourd'hui"
        : `Votre abonnement se termine dans ${left} jour${left > 1 ? "s" : ""}`;
  const contact = [notice.phone, notice.email].filter(Boolean).join(" · ");
  return (
    <div data-testid="subscription-notice" className={`border rounded-sm p-4 sm:p-5 mb-6 flex gap-3 ${NOTICE_STYLES[notice.level]}`}>
      <CalendarClock className="h-5 w-5 shrink-0 mt-0.5" strokeWidth={1.75} />
      <div className="text-sm">
        <p className="font-semibold">{title}</p>
        <p className="mt-1 opacity-90">
          {notice.started_at && <>Souscrit le {fmtDate(notice.started_at)}. </>}
          {notice.expires_at && <>Fin le {fmtDate(notice.expires_at)}. </>}
          {notice.level !== "blocked" && "Sans renouvellement, la boutique sera suspendue automatiquement le lendemain. "}
          {contact && <>Pour renouveler : <strong>{contact}</strong></>}
        </p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/subscription/notice").then(({ data }) => setNotice(data.notice)).catch(() => {});
  }, []);

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Administration</p>
      <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight mb-8">Tableau de bord</h1>

      {notice && <SubscriptionNotice notice={notice} />}

      {!stats ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-white border border-stone animate-pulse rounded-sm" />)}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Stat testid="stat-revenue" icon={Euro} label="Chiffre d'affaires" value={eur(stats.revenue)} />
            <Stat testid="stat-orders" icon={ShoppingCart} label="Commandes payées" value={stats.total_orders} />
            <Stat testid="stat-products" icon={Package} label="Produits" value={stats.total_products} />
            <Stat testid="stat-customers" icon={Users} label="Clients" value={stats.total_customers} />
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-6">
            <Link to="/admin/produits" data-testid="dash-link-products" className="group bg-ink text-cream rounded-sm p-8 flex items-center justify-between hover:bg-[#2C2B29] transition-colors">
              <div>
                <h2 className="font-serif text-2xl">Gérer les produits</h2>
                <p className="text-cream/60 text-sm mt-1">Ajouter, modifier ou retirer un luminaire.</p>
              </div>
              <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/admin/commandes" data-testid="dash-link-orders" className="group bg-white border border-stone rounded-sm p-8 flex items-center justify-between hover:border-ink transition-colors">
              <div>
                <h2 className="font-serif text-2xl text-ink">Gérer les commandes</h2>
                <p className="text-graphite text-sm mt-1">Suivre et mettre à jour les statuts.</p>
              </div>
              <ArrowRight className="h-6 w-6 text-ink group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
